import type { AppConfigDocument } from "./generated/app-config-document.js";
import { assertAppConfigStructure } from "./structural-validation.js";

export const APP_CONFIG_SCHEMA_VERSION = 1 as const;

export type DailyActivityConfig = AppConfigDocument["dailyActivity"];
export type StreakRuleVersionConfig = DailyActivityConfig["ruleVersions"][number];
export type LearningPaceConfig = StreakRuleVersionConfig["paces"][number];
export type ReferenceLevelConfig = AppConfigDocument["referenceLevels"][number];
export type JourneyTierConfig = AppConfigDocument["journeyTiers"][number];
export type { AppConfigDocument };

export class AppConfigValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AppConfigValidationError";
  }
}

const assertCanonicalString = (value: string, path: string): void => {
  if (value.trim() !== value || value === "") {
    throw new AppConfigValidationError(
      `${path} must be a non-empty string without surrounding whitespace`,
    );
  }
};

const assertSafeInteger = (value: number, path: string): void => {
  if (!Number.isSafeInteger(value)) {
    throw new AppConfigValidationError(`${path} must be a safe integer`);
  }
};

/** The rule version in effect on `day` (`YYYY-MM-DD`); days before the first version use the first. */
export function streakRuleVersionForDay(
  dailyActivity: DailyActivityConfig,
  day: string,
): StreakRuleVersionConfig {
  const versions = dailyActivity.ruleVersions;
  return [...versions].reverse().find((version) => version.effectiveFrom <= day) ?? versions[0];
}

/**
 * A pace a learner may select on `day`: one in the version in effect then or in a later (scheduled) version. Callers
 * without the learner's study day pass a day early enough to cover every time zone.
 */
export function learningPaceForId(
  dailyActivity: DailyActivityConfig,
  paceId: string,
  day: string,
): LearningPaceConfig | null {
  const current = streakRuleVersionForDay(dailyActivity, day);
  return (
    dailyActivity.ruleVersions
      .filter((version) => version.effectiveFrom >= current.effectiveFrom)
      .flatMap((version) => version.paces)
      .find((pace) => pace.id === paceId) ?? null
  );
}

export function parseAppConfigDocument(input: unknown): AppConfigDocument {
  assertAppConfigStructure(input, AppConfigValidationError);
  const config = input;
  assertCanonicalString(config.revision, "appConfig.revision");

  for (const [versionIndex, version] of config.dailyActivity.ruleVersions.entries()) {
    const versionPath = `appConfig.dailyActivity.ruleVersions[${versionIndex}]`;
    const previous = config.dailyActivity.ruleVersions[versionIndex - 1];
    if (previous && version.effectiveFrom <= previous.effectiveFrom) {
      throw new AppConfigValidationError(
        `${versionPath}.effectiveFrom must be after the previous version's`,
      );
    }
    assertSafeInteger(version.keptDaysPerPause, `${versionPath}.keptDaysPerPause`);
    assertSafeInteger(version.maxBankedPauses, `${versionPath}.maxBankedPauses`);
    for (const [index, pace] of version.paces.entries()) {
      const path = `${versionPath}.paces[${index}]`;
      assertCanonicalString(pace.id, `${path}.id`);
      assertCanonicalString(pace.title, `${path}.title`);
      assertSafeInteger(pace.wordsPerDay, `${path}.wordsPerDay`);
    }
    const paceIds = version.paces.map((pace) => pace.id);
    if (new Set(paceIds).size !== paceIds.length) {
      throw new AppConfigValidationError(`${versionPath}.paces ids must be unique`);
    }
    const targets = version.paces.map((pace) => pace.wordsPerDay);
    if (targets.some((target, index) => index > 0 && target <= targets[index - 1])) {
      throw new AppConfigValidationError(
        `${versionPath}.paces wordsPerDay must be strictly ascending`,
      );
    }
  }

  if (config.referenceLevels[0].wordsKnown !== 0) {
    throw new AppConfigValidationError(
      "appConfig.referenceLevels[0].wordsKnown must be 0",
    );
  }
  const levelIds = new Set<string>();
  for (const [index, level] of config.referenceLevels.entries()) {
    const path = `appConfig.referenceLevels[${index}]`;
    assertSafeInteger(level.wordsKnown, `${path}.wordsKnown`);
    assertCanonicalString(level.id, `${path}.id`);
    assertCanonicalString(level.title, `${path}.title`);
    assertCanonicalString(level.badgeAssetKey, `${path}.badgeAssetKey`);
    if (level.description !== null) {
      assertCanonicalString(level.description, `${path}.description`);
    }
    if (levelIds.has(level.id)) {
      throw new AppConfigValidationError(
        "appConfig.referenceLevels ids must be unique",
      );
    }
    levelIds.add(level.id);
    if (
      index > 0
      && level.wordsKnown <= config.referenceLevels[index - 1].wordsKnown
    ) {
      throw new AppConfigValidationError(
        "appConfig.referenceLevels wordsKnown must be strictly increasing",
      );
    }
  }

  const lastJourneyIndex = config.journeyTiers.length - 1;
  for (const [index, tier] of config.journeyTiers.entries()) {
    const path = `appConfig.journeyTiers[${index}]`;
    const hasGap = tier.gap !== undefined;
    const hasLevels = tier.levels !== undefined;
    if (hasGap !== hasLevels) {
      throw new AppConfigValidationError(
        `${path} must include both gap and levels, or neither`,
      );
    }
    if (index === lastJourneyIndex) {
      if (hasGap || hasLevels) {
        throw new AppConfigValidationError(
          `${path} must be terminal and omit gap and levels`,
        );
      }
    } else if (tier.gap === undefined || tier.levels === undefined) {
      throw new AppConfigValidationError(
        `${path} must include gap and levels`,
      );
    } else {
      assertSafeInteger(tier.gap, `${path}.gap`);
      assertSafeInteger(tier.levels, `${path}.levels`);
    }
  }

  for (const [lang, locations] of Object.entries(config.journeyLocations)) {
    const path = `appConfig.journeyLocations.${lang}`;
    if (locations.length !== config.journeyTiers.length) {
      throw new AppConfigValidationError(
        `${path} must have one location per journey tier`,
      );
    }
    const locationIds = new Set<string>();
    for (const [index, location] of locations.entries()) {
      assertCanonicalString(location.id, `${path}[${index}].id`);
      assertCanonicalString(location.name, `${path}[${index}].name`);
      assertCanonicalString(
        location.nativeName,
        `${path}[${index}].nativeName`,
      );
      assertCanonicalString(
        location.description,
        `${path}[${index}].description`,
      );
      if (locationIds.has(location.id)) {
        throw new AppConfigValidationError(`${path} ids must be unique`);
      }
      locationIds.add(location.id);
    }
  }

  for (const [index, tip] of config.reviewLoadingTips.entries()) {
    assertCanonicalString(tip, `appConfig.reviewLoadingTips[${index}]`);
  }

  return config;
}
